import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billingService';
import Navbar from '../components/Navbar';

const Pricing = () => {
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: '/month',
      description: '100 API calls / month, basic dashboard access',
      features: [
        '100 API calls per month',
        'Basic Sybil detection',
        'Dashboard access',
        'Community support',
        'Rate limit: 10 req/min'
      ],
      cta: 'Start Free',
      highlight: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$99',
      period: '/month',
      description: '50,000 API calls / month, CSV export',
      features: [
        '50,000 API calls per month',
        'Advanced Sybil detection',
        'Full dashboard access',
        'CSV/JSON export',
        'Email support',
        'Rate limit: 100 req/min',
        'Historical data access'
      ],
      cta: 'Upgrade to Pro',
      highlight: true,
      stripeId: 'basic'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'Unlimited + SLA + priority support',
      features: [
        'Unlimited API calls',
        'Real-time Sybil detection',
        'Custom integrations',
        'Dedicated support',
        'SLA guarantee',
        'Rate limit: 1000 req/min',
        'On-premise deployment option',
        'Custom false positive review'
      ],
      cta: 'Contact Sales',
      highlight: false,
      stripeId: 'enterprise'
    }
  ];

  const handleSelectPlan = async (tier) => {
    if (!user) {
      // Redirect to register with plan info
      navigate(`/register?plan=${tier.id}`);
      return;
    }

    if (tier.id === 'free') {
      navigate('/api-dashboard');
      return;
    }

    if (tier.id === 'enterprise' || !tier.stripeId) {
      // Contact sales
      window.location.href = 'mailto:sales@frwdlabs.xyz?subject=Enterprise Plan Inquiry';
      return;
    }

    // Stripe checkout for paid plans
    setLoading(tier.id);
    try {
      const result = await billingService.createCheckout(tier.stripeId);
      if (result.ok && result.url) {
        window.location.href = result.url;
      } else {
        alert('Failed to start checkout. Please try again.');
        setLoading(null);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="bg-dark text-light" style={{ minHeight: '100vh' }}>
      {user ? <Navbar /> : (
        <nav className="navbar navbar-dark bg-black">
          <div className="container-fluid">
            <a className="navbar-brand" href="/">frwd labs</a>
            <div className="d-flex gap-2">
              <a href="/login.html" className="btn btn-outline-light btn-sm">Login</a>
            </div>
          </div>
        </nav>
      )}

      <div className="container my-5">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-4 mb-3">Pricing Plans</h1>
          <p className="lead text-secondary">
            Choose the plan that fits your needs. All plans include access to our Sybil detection API.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="row g-4 mb-5">
          {tiers.map((tier) => (
            <div key={tier.id} className="col-12 col-md-6 col-lg-4">
              <div
                className={`card h-100 ${tier.highlight ? 'bg-primary border-primary' : 'bg-secondary-subtle'} text-light`}
                style={tier.highlight ? { borderWidth: '2px', transform: 'scale(1.05)' } : {}}
              >
                <div className="card-body d-flex flex-column">
                  {tier.highlight && (
                    <div className="text-center mb-2">
                      <span className="badge bg-warning text-dark">Most Popular</span>
                    </div>
                  )}

                  <h3 className="card-title mb-2">{tier.name}</h3>

                  <div className="mb-3">
                    <h2 className="mb-0">
                      {tier.price}
                      {tier.period && (
                        <small className={`${tier.highlight ? 'text-light' : 'text-secondary'} fs-6`}>
                          {tier.period}
                        </small>
                      )}
                    </h2>
                    <p className={`small ${tier.highlight ? 'text-light' : 'text-secondary'} mt-2`}>
                      {tier.description}
                    </p>
                  </div>

                  <ul className="list-unstyled mb-4">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="mb-2">
                        <i className={`bi bi-check-circle-fill ${tier.highlight ? 'text-light' : 'text-success'} me-2`}></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`btn ${tier.highlight ? 'btn-light' : tier.id === 'enterprise' ? 'btn-warning' : 'btn-outline-light'} w-100 mt-auto`}
                    onClick={() => handleSelectPlan(tier)}
                    disabled={loading !== null}
                  >
                    {loading === tier.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processing...
                      </>
                    ) : (
                      tier.cta
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ / Additional Info */}
        <div className="row justify-content-center mt-5">
          <div className="col-lg-8">
            <div className="card bg-secondary-subtle text-light">
              <div className="card-body">
                <h4 className="mb-4">Frequently Asked Questions</h4>

                <div className="mb-3">
                  <h6>Can I change plans later?</h6>
                  <p className="text-secondary small mb-0">
                    Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                  </p>
                </div>

                <div className="mb-3">
                  <h6>What happens if I exceed my API limit?</h6>
                  <p className="text-secondary small mb-0">
                    API requests will return a 429 rate limit error. You can upgrade your plan for higher limits.
                  </p>
                </div>

                <div className="mb-3">
                  <h6>Do you offer refunds?</h6>
                  <p className="text-secondary small mb-0">
                    Yes, we offer a 14-day money-back guarantee on all paid plans, no questions asked.
                  </p>
                </div>

                <div className="mb-3">
                  <h6>How is the API billed?</h6>
                  <p className="text-secondary small mb-0">
                    Monthly subscriptions are billed at the beginning of each billing cycle. Usage resets monthly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center mt-5">
          <p className="text-secondary">
            Have questions? <a href="mailto:sales@frwdlabs.xyz" className="text-primary">Contact our sales team</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;


